import type { ChannelSnapshotData } from "containers/Channels/types";
import type { ImportBatchSnapshotsPayload } from "containers/Titles/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { fetchSubscriptionChannel, importChannelSnapshot } from "store/channelStore";
import { fetchProcessingJobs } from "store/jobsStore";
import type { TPeriod } from "store/periodStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { importBatchSnapshots } from "store/titleStore";
import { ChannelCalculationMethod } from "store/types";

import SubscriptionManagementModalUI from "./index";
import { useSubscriptionTablePagination } from "./useSubscriptionTablePagination";

interface SubscriptionManagementModalContainerProps {
    onClose: () => void;
    selectedPeriod: TPeriod;
    onOpenImport?: () => void;
    isLoadingComposites?: boolean;
}

// Main container component for subscription management modal
const SubscriptionManagementModalContainer = ({
    onClose,
    selectedPeriod,
    onOpenImport,
    isLoadingComposites = false,
}: SubscriptionManagementModalContainerProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { periods, periodsWithComposites } = useAppSelector((state) => state.period);
    const { channels, importChannelSnapshotStatus } = useAppSelector((state) => state.channels);
    const { processingJobs, processingJobsStatus } = useAppSelector((state) => state.jobs);
    const { importBatchSnapshotsStatus } = useAppSelector((state) => state.titles);
    const [channelSnapshot, setChannelSnapshot] = useState<ChannelSnapshotData | null>(null);
    const [totalSubscriptionPoints, setTotalSubscriptionPoints] = useState<number>(0);
    const [isLoadingSnapshot, setIsLoadingSnapshot] = useState(false);
    const [changedRows, setChangedRows] = useState<
        Map<number, { consumptionCount: number; avgConsumptionDuration: number; coefficient: number }>
    >(new Map());
    const [totalRevenue, setTotalRevenue] = useState<string>("");
    const [isTotalRevenueChanged, setIsTotalRevenueChanged] = useState(false);
    const [searchQuery, setSearchQuery] = useState<string>("");

    useEffect(() => {
        dispatch(fetchProcessingJobs());
    }, [dispatch]);

    // Check if subscription or sales jobs are in progress OR if save operations are pending
    const isProcessingJobInProgress = useMemo(() => {
        if (processingJobsStatus === "pending") return false;

        const hasActiveJob = processingJobs.some(
            (job) => job.importType === "subscription-channel-recalculate" || job.importType === "sales",
        );

        const isSaving = importChannelSnapshotStatus === "pending" || importBatchSnapshotsStatus === "pending";

        return hasActiveJob || isSaving;
    }, [processingJobs, processingJobsStatus, importChannelSnapshotStatus, importBatchSnapshotsStatus]);

    const activePeriods = periods.filter((period) => period.isActive === 1);

    const hasCompositePeriods = useMemo(() => {
        const periodWithComposites = periodsWithComposites.find((pwc) => pwc.composite.id === selectedPeriod.id);
        return periodWithComposites ? periodWithComposites.components.length > 0 : false;
    }, [periodsWithComposites, selectedPeriod.id]);

    const subscriptionChannels = channels.filter(
        (channel) => channel.calculationMethod === ChannelCalculationMethod.SUBSCRIPTION,
    );

    const channelId = subscriptionChannels[0]?.id;
    const periodId = selectedPeriod.id;

    // Use pagination hook for table data (only enabled when composites are loaded)
    const { page, rowsPerPage, totalCount, isLoading, data, handleChangePage, handleChangeRowsPerPage } =
        useSubscriptionTablePagination({
            channelId: channelId || 0,
            periodId: periodId || 0,
            totalSubscriptionPoints,
            defaultRowsPerPage: 10,
            enabled: !isLoadingComposites,
            query: searchQuery,
            periodsWithComposites,
        });

    // Fetch subscription channel data (only when composites are loaded)
    useEffect(() => {
        if (channelId && periodId && !isLoadingComposites) {
            setIsLoadingSnapshot(true);
            dispatch(fetchSubscriptionChannel({ channelId, periodId }))
                .unwrap()
                .then((response) => {
                    setChannelSnapshot(response.channelSnapshot);
                    setTotalSubscriptionPoints(response.totalSubscriptionPoints);
                })
                .catch((error) => {
                    console.error("Failed to fetch subscription channel:", error);
                })
                .finally(() => {
                    setIsLoadingSnapshot(false);
                });
        }
    }, [channelId, periodId, dispatch, isLoadingComposites]);

    // Initialize totalRevenue from channelSnapshot
    useEffect(() => {
        if (channelSnapshot?.totalRevenue !== undefined) {
            setTotalRevenue(channelSnapshot.totalRevenue.toString());
            setIsTotalRevenueChanged(false);
        }
    }, [channelSnapshot]);

    const handleTotalRevenueChange = useCallback((value: string) => {
        setTotalRevenue(value);
        setIsTotalRevenueChanged(true);
    }, []);

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    }, []);

    const handleCellSave = useCallback(
        (rowId: string | number, columnId: string, value: any) => {
            const titleId = Number(rowId);
            setChangedRows((prev) => {
                const updated = new Map(prev);

                // Get existing snapshot or find the row in current data
                let snapshot = updated.get(titleId);
                if (!snapshot) {
                    const row = data.find((r) => r.id === titleId);
                    if (!row) return prev; // Row not found, don't update

                    snapshot = {
                        consumptionCount: row.consumptionCount ?? 0,
                        avgConsumptionDuration: row.avgConsumptionDuration ?? 0,
                        coefficient: row.coefficient ?? 1,
                    };
                }

                // Update the specific field
                snapshot = { ...snapshot, [columnId]: value };
                updated.set(titleId, snapshot);
                return updated;
            });
        },
        [data],
    );

    const handleSave = useCallback(async () => {
        if (changedRows.size === 0 && !isTotalRevenueChanged) {
            toast.info(`${t("Common.NoChangesToSave")}`);
            return;
        }

        try {
            if (isTotalRevenueChanged) {
                await dispatch(
                    importChannelSnapshot({
                        channelId,
                        periodId,
                        totalRevenue: parseFloat(totalRevenue) || 0,
                        currency: channelSnapshot?.currency || "EUR",
                    }),
                ).unwrap();
            }

            if (changedRows.size > 0) {
                const titleSnapshots = Array.from(changedRows.entries()).map(([titleId, snapshot]) => ({
                    titleId,
                    channelId,
                    periodId,
                    avgConsumptionDuration: snapshot.avgConsumptionDuration,
                    consumptionCount: snapshot.consumptionCount,
                    coefficient: snapshot.coefficient,
                }));

                const payload: ImportBatchSnapshotsPayload = {
                    titleSnapshots,
                };

                await dispatch(importBatchSnapshots(payload)).unwrap();
            }

            toast.success(`${t("Subscription.ChangesSavedSuccessfully")}`);
            setChangedRows(new Map());
            setIsTotalRevenueChanged(false);
        } catch (error) {
            toast.error(`${t("Subscription.ErrorSavingChanges")}`);
        }
    }, [changedRows, isTotalRevenueChanged, totalRevenue, channelSnapshot, channelId, periodId, dispatch, t]);

    if (!channelId) {
        return null;
    }

    return (
        <SubscriptionManagementModalUI
            onClose={onClose}
            selectedPeriod={selectedPeriod}
            activePeriods={activePeriods}
            subscriptionChannels={subscriptionChannels}
            channelId={channelId}
            channelSnapshot={channelSnapshot}
            totalSubscriptionPoints={totalSubscriptionPoints}
            isLoadingSnapshot={isLoadingSnapshot}
            hasCompositePeriods={hasCompositePeriods}
            tableData={data}
            tableIsLoading={isLoading}
            tablePage={page}
            tableRowsPerPage={rowsPerPage}
            tableTotalCount={totalCount}
            totalRevenue={totalRevenue}
            changedRows={changedRows}
            isTotalRevenueChanged={isTotalRevenueChanged}
            isProcessingJobInProgress={isProcessingJobInProgress}
            isLoadingComposites={isLoadingComposites}
            searchQuery={searchQuery}
            onTotalRevenueChange={handleTotalRevenueChange}
            onCellSave={handleCellSave}
            onSave={handleSave}
            onTablePageChange={handleChangePage}
            onTableRowsPerPageChange={handleChangeRowsPerPage}
            onSearchChange={handleSearchChange}
            onOpenImport={onOpenImport}
        />
    );
};

export default SubscriptionManagementModalContainer;
